import {
  Button,
  Icon,
  IconButton,
  NameAvatar,
  Typography,
  VerticalDivider
} from '@smartgift/frontend-toolkit';
import dayjs from 'dayjs';
import React, { useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { DownloadReport } from '@hero/components/modals';
import { OrderStatusBadge, Table } from '@hero/components/molecules';
import { GIFT_STATUSES, NON_EDITABLE_AUTOMATION_STATUS, dateLabelFormatDay } from '@hero/constants';
import { useSendRemindersNotification } from '@hero/hooks';
import { ROUTE_PATHS } from '@hero/routes';
import { useOrderReport } from '@hero/services/analytic-service/hooks';
import {
  useGetAutomationOrders,
  useGetUpcomingSends,
  useSendRemindersAutomation,
  useDeleteAutomationOrder
} from '@hero/services/order-service';
import { useGetAcceptanceRate } from '@hero/services/order-service/hook';
import { useNotification } from '@hero/store/hooks';
import { cleanObjectEmptyFields, formatDate, formatNumber } from '@hero/utils';
import { removeOrderSchema } from '@hero/validations';

import * as S from './style';

function AutomationGiftActivity({ automation }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const urlParams = useParams();
  const { showNotification } = useNotification();
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);

  const occasionType = automation?.type === 'birthday' ? 'birthday' : 'anniversary';

  const [globalFilters, setGlobalFilters] = useState({});
  const [orderGlobalFilters, setOrderGlobalFilters] = useState({});
  const [{ pageIndex: upcomingPageIndex, pageSize: upcomingPageSize }, setUpcomingPagination] =
    useState({
      pageIndex: 0,
      pageSize: 10
    });
  const [{ pageIndex: previousPageIndex, pageSize: previousPageSize }, setPreviousPagination] =
    useState({
      pageIndex: 0,
      pageSize: 10
    });

  const { data: upcomingSends } = useGetUpcomingSends(
    {
      code: automation?.code,
      filters: {
        searchTerm: globalFilters.searchTerm
      },
      pagination: {
        page: upcomingPageIndex + 1,
        limit: upcomingPageSize
      }
    },
    {
      enabled: !!automation?.code
    }
  );

  const { data: orders } = useGetAutomationOrders(
    automation?.code,
    {
      filters: cleanObjectEmptyFields({
        searchTerm: orderGlobalFilters?.searchTerm
      }),
      pagination: {
        page: previousPageIndex + 1,
        limit: previousPageSize
      }
    },
    {
      enabled: !!automation?.code
    }
  );

  const { data: automationAcceptanceRate } = useGetAcceptanceRate(urlParams?.code, {
    filters: {
      associationType: 'automation'
    }
  });

  const { mutateAsync: deleteOrder } = useDeleteAutomationOrder();

  const onDeleteModal = orderCode => {
    showNotification({
      title: t('areYouSureRemoveOrder'),
      message: t('areYouSureRemoveRecipientOrderMessage'),
      notificationImage: <Icon name="error" size="xxl" color="var(--color-error-default)" />,
      formData: [
        {
          name: 'confirm',
          title: (
            <Typography variant="body2Bold" style={{ marginBottom: 4 }}>
              {t('removeOrderModalConfirmLabel')}
            </Typography>
          ),
          type: 'text'
        }
      ],
      formValidationSchema: removeOrderSchema,
      secondaryButtonProps: { children: t('cancel') },
      primaryButtonProps: {
        color: 'error',
        children: t('yesRemove'),
        onClick: async () => {
          await deleteOrder({ code: automation.code, orderCode });
        }
      }
    });
  };

  const upcomingTableColumns = React.useMemo(
    () => [
      {
        header: t('name'),
        accessorFn: originalRow => (
          <Button
            variant="link"
            color="neutral"
            onClick={() => {
              navigate(ROUTE_PATHS.CONTACT_DETAIL(originalRow.id));
            }}
            style={{ textAlign: 'left' }}
          >
            <NameAvatar name={`${originalRow?.firstName} ${originalRow?.lastName}`} />
            <p>{`${originalRow?.firstName} ${originalRow?.lastName}`}</p>
          </Button>
        )
      },
      {
        header: t('email'),
        accessorFn: originalRow => <S.Email>{originalRow?.email}</S.Email>
      },
      {
        header: t('sendDate'),
        accessorFn: originalRow => {
          const date = dayjs(originalRow[occasionType]?.full).subtract(
            automation?.settings?.occasionScheduleDuration?.value,
            automation?.settings?.occasionScheduleDuration?.unit
          );

          return originalRow[occasionType] ? date : null;
        },
        cell: ({ cell }) => {
          return (
            <S.Email>
              {cell.getValue()
                ? formatDate(cell.getValue(), { format: dateLabelFormatDay, includeTime: false })
                : '---'}
            </S.Email>
          );
        }
      },
      {
        ...(automation?.type === 'birthday'
          ? {
              header: t('birthday'),
              accessorFn: originalRow => {
                return dayjs(originalRow.birthday.full);
              },
              cell: ({ cell }) => {
                return (
                  <S.Email>
                    {formatDate(cell.getValue(), {
                      format: dateLabelFormatDay,
                      includeTime: false
                    })}
                  </S.Email>
                );
              }
            }
          : {
              header: t('workAnniversary'),
              accessorFn: originalRow => {
                return dayjs(originalRow.anniversary.full);
              },
              cell: ({ cell }) => {
                return (
                  <S.Email>
                    {formatDate(cell.getValue(), {
                      format: dateLabelFormatDay,
                      includeTime: false
                    })}
                  </S.Email>
                );
              }
            })
      }
    ],
    []
  );

  const previousTableColumns = React.useMemo(
    () => [
      {
        header: t('name'),
        accessorFn: originalRow => {
          return {
            recipient: originalRow.recipient,
            code: originalRow.code
          };
        },
        cell: ({ cell: { getValue } }) => {
          const { recipient, code: orderCode } = getValue();
          return (
            <Button
              variant="link"
              color="neutral"
              onClick={() => {
                navigate(ROUTE_PATHS.AUTOMATION_RECIPIENT_DETAIL(urlParams?.code, orderCode));
              }}
              style={{ textAlign: 'left' }}
            >
              <NameAvatar name={`${recipient?.firstName} ${recipient?.lastName}`} />
              <p>{`${recipient?.firstName} ${recipient?.lastName}`}</p>
            </Button>
          );
        }
      },
      {
        header: t('email'),
        accessorFn: originalRow => {
          return originalRow.recipient?.email;
        },
        cell: ({ cell }) => {
          return <S.Email>{cell.getValue()}</S.Email>;
        }
      },
      {
        header: t('sentDate'),
        accessorFn: originalRow => {
          const emailSent = originalRow?.timeline?.find(
            timeline => timeline.action === GIFT_STATUSES.emailSent
          );
          return emailSent?.date;
        },
        cell: ({ cell }) => {
          return (
            <S.Email>
              {formatDate(cell.getValue(), { format: dateLabelFormatDay, includeTime: false })}
            </S.Email>
          );
        }
      },
      {
        header: t('giftStatus'),
        accessorFn: originalRow => ({
          status: originalRow.status,
          giftStatus: originalRow.giftStatus
        }),
        cell: ({ cell: { getValue } }) => {
          return <OrderStatusBadge status={getValue().giftStatus} />;
        }
      },
      {
        id: 'actions',
        header: '',
        size: 50,
        cell: ({ row }) => {
          return (
            <IconButton
              variant="text"
              color="neutral"
              onClick={() => onDeleteModal(row.original.code)}
              disabled={row.original.status?.accepted === true}
            >
              <Icon name="delete" size="md" />
            </IconButton>
          );
        }
      }
    ],
    []
  );

  const isButtonDisabled = React.useMemo(
    () => NON_EDITABLE_AUTOMATION_STATUS.includes(automation?.status),
    [automation]
  );

  const { send } = useSendRemindersNotification({ useSendReminder: useSendRemindersAutomation });

  const handleSendReminder = () => {
    send(
      automation?.code,
      orders?.docs?.filter(item => !item?.status?.accepted).map(item => item.code)
    );
  };

  const { mutateAsync: generateReport } = useOrderReport();

  const isReminderActive = useMemo(() => {
    const totalRecipients = automationAcceptanceRate?.acceptanceRates?.total;
    const totalAcceptedRecipients = automationAcceptanceRate?.acceptanceRates?.accepted;

    if (totalRecipients === 0) return false;
    if (totalAcceptedRecipients === 0) return true;

    return totalRecipients - totalAcceptedRecipients > 0;
  }, [automationAcceptanceRate]);

  return (
    <S.GiftActivityWrapper>
      <S.Header style={{ marginBottom: 8 }}>
        <S.HeaderLeft>
          <Typography as="div" variant="body2" style={{ display: 'flex', gap: 4 }}>
            <Typography variant="body2Bold">{t('upcomingSends')}</Typography> ({t('inFourWeeks')})
          </Typography>
          <VerticalDivider $margin="0 10px" $height={24} />
          <Typography variant="body2">
            <Trans
              i18nKey="total"
              values={{
                count: formatNumber(upcomingSends?.docs?.length)
              }}
              components={{
                b: <b />
              }}
            />
          </Typography>
        </S.HeaderLeft>
      </S.Header>

      <Table
        data={upcomingSends?.docs || []}
        columns={upcomingTableColumns}
        globalFilters={globalFilters}
        setGlobalFilters={setGlobalFilters}
        pagination={{
          pageIndex: upcomingPageIndex,
          pageSize: upcomingPageSize,
          total: upcomingSends?.pagination?.totalDocs
        }}
        enabled={{ header: true }}
        setPagination={setUpcomingPagination}
        manualPagination
        rowHeight={52}
        pageCount={upcomingSends?.pagination?.totalPages}
      />

      <S.Header style={{ marginTop: 24 }}>
        <S.HeaderLeft>
          <Typography variant="body2Bold">{t('previousSends')}</Typography>
          <VerticalDivider $margin="0 10px" $height={24} />
          <Typography variant="body2">
            <Trans
              i18nKey="total"
              values={{
                count: formatNumber(orders?.totalDocs)
              }}
              components={{
                b: <b />
              }}
            />
          </Typography>
          {isReminderActive && (
            <>
              <VerticalDivider $margin="0 10px" $height={24} />
              <Button variant="link" onClick={handleSendReminder} disabled={isButtonDisabled}>
                <Icon name="email" color="var(--color-accent-primary-default)" />
                {t('remindRecipients')}
              </Button>
            </>
          )}
        </S.HeaderLeft>
        <Button
          variant="text"
          startIcon={<Icon name="download" />}
          onClick={() => setIsDownloadModalOpen(true)}
          disabled={!automation || !orders?.totalDocs}
        >
          {t('downloadReport')}
        </Button>
      </S.Header>

      <Table
        data={orders?.docs || []}
        columns={previousTableColumns}
        globalFilters={orderGlobalFilters}
        setGlobalFilters={setOrderGlobalFilters}
        pagination={{
          pageIndex: previousPageIndex,
          pageSize: previousPageSize,
          total: orders?.totalDocs
        }}
        enabled={{ header: true }}
        setPagination={setPreviousPagination}
        manualPagination
        rowHeight={52}
        pageCount={orders?.totalPages}
      />
      {isDownloadModalOpen && (
        <DownloadReport
          isOpen={isDownloadModalOpen}
          onRequestClose={setIsDownloadModalOpen}
          onAsyncSubmit={async data => {
            await generateReport({
              emails: data.contacts.map(item => item.email),
              filters: {
                associationType: 'automation',
                associationCode: automation.code
              }
            });
          }}
        />
      )}
    </S.GiftActivityWrapper>
  );
}

export { AutomationGiftActivity };
